/**
 * Brand Login Mock — props-driven non-functional login preview.
 *
 * Renders the three templates (centered, split-hero, backdrop) from brand config
 * props, aligned to saas_login's actual rendering code (0019 fidelity baseline:
 * saas_login@17e6e51 — AuthLayout, SplitHero, Backdrop, Login, AuthCard,
 * BrandedLogo, authTemplate.ts).
 *
 * Extracted from BrandLoginPreview (0014's public localStorage route); reusable
 * by the edit-in-preview Brand Editor (0020+). All inputs/buttons inert.
 *
 * Spec 0021: Edit-mode slots — optional `editMode` props enable click-to-edit slots
 * (hover → outline + label; click → callback). Inert when absent (public route, pop-out).
 */
import { type ReactNode } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';

export interface BrandMockConfig {
  displayName: string;
  shortName?: string;
  logoUrl?: string;
  colors: Record<string, string>;
  fonts: Record<string, string>;
  template?: 'centered' | 'split-hero' | 'backdrop' | '';
  tagline?: string;
  heroCopy?: string;
  location?: string;
  heroImageUrl?: string;
  primaryColorHover?: string;
  authProviders: string[];
  signupEnabled: boolean;
  passwordResetEnabled: boolean;
  // Spec 0030 — guest-signup affordance: a plain on/off toggle; the link text is
  // the product's label (falling back to "Continue as guest" at render). No URL —
  // the mock link is inert.
  guestSignupEnabled?: boolean;
  guestSignupLabel?: string;
  copyStrings?: Record<string, string>;
}

// Spec 0021 — Edit-mode slot hooks (image slots)
// Spec 0022 — text slots (heading/subtitle/tagline/heroCopy/location) +
// color slots (signInButton/pageBackground/cardBorder) wired onto the same framework.
// Spec 0023 — toggle slots (googleButton/signupLink/forgotPassword/guestSignup),
// ghosted-when-off in edit mode; fonts ride the heading/subtitle popovers.
export type SlotField =
  | 'heroImage'
  | 'logo'
  | 'favicon'
  | 'heading'
  | 'subtitle'
  | 'tagline'
  | 'heroCopy'
  | 'location'
  | 'signInButton'
  | 'pageBackground'
  | 'cardBorder'
  | 'googleButton'
  | 'signupLink'
  | 'forgotPassword'
  | 'guestSignup';
export type SlotState = 'active' | 'ghosted'; // ghosted = ~40% opacity + "off" chip (0023)

export interface EditModeHooks {
  onSlotClick: (field: SlotField, anchorEl: HTMLElement) => void;
  getSlotState?: (field: SlotField) => SlotState; // optional; defaults to 'active'
  getSlotLabel: (field: SlotField) => string;
}

/**
 * Spec 0027 — virtual viewport. The mock lays out against this fixed desktop
 * design size (matching saas_login at desktop width). The editor scales the whole
 * box to fit (or shows it 1:1 in a scrollable frame); the pop-out route omits
 * `viewport` and fills the real window as before.
 */
export const PREVIEW_VIEWPORT = { width: 1440, height: 900 } as const;

export interface BrandLoginMockProps {
  config: BrandMockConfig;
  editMode?: EditModeHooks; // When absent, render inert (public route, pop-out)
  // When set, render into a fixed-size virtual viewport (desktop layout, no window
  // media queries). When absent, fill the real window (minHeight:100vh, responsive).
  viewport?: { width: number; height: number };
}

/**
 * Slot wrapper — hover → outline + label chip; click → onSlotClick.
 * Ghosted state: ~40% opacity + "off" chip (clickable, for 0023).
 */
interface SlotProps {
  field: SlotField;
  editMode?: EditModeHooks;
  children: ReactNode;
  // Spec 0027 — when this slot wraps a layout region that must fill its flex
  // parent (page background, hero panel, backdrop), the edit-mode wrapper Box
  // would otherwise break the flex/size passthrough. `fill` makes it grow +
  // become a flex container so the child's own sizing still applies. Inert mode
  // renders children directly, so no fill wrapper is needed there.
  fill?: boolean;
}

function Slot({ field, editMode, children, fill }: SlotProps) {
  if (!editMode) {
    // Inert mode (public route, pop-out) — render children as-is
    return <>{children}</>;
  }

  const state = editMode.getSlotState?.(field) ?? 'active';
  const label = editMode.getSlotLabel(field);
  const isGhosted = state === 'ghosted';

  return (
    <Box
      data-slot={field}
      onClick={(e) => {
        e.stopPropagation();
        editMode.onSlotClick(field, e.currentTarget as HTMLElement);
      }}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        opacity: isGhosted ? 0.4 : 1,
        transition: 'opacity 0.2s',
        ...(fill && { display: 'flex', flexGrow: 1, alignSelf: 'stretch', minWidth: 0 }),
        '&:hover': {
          outline: '2px dashed',
          outlineColor: 'primary.main',
          outlineOffset: '2px',
          '& .slot-label': {
            opacity: 1,
          },
        },
      }}
    >
      {children}
      {/* Label chip — active: visible on hover; ghosted: always visible "off" cue (0023) */}
      <Chip
        className="slot-label"
        label={isGhosted ? `${label} (off)` : label}
        size="small"
        sx={{
          position: 'absolute',
          top: 4,
          left: 4,
          opacity: isGhosted ? 1 : 0,
          transition: 'opacity 0.2s',
          pointerEvents: 'none',
          bgcolor: isGhosted ? 'grey.700' : 'primary.main',
          color: isGhosted ? 'common.white' : 'primary.contrastText',
          fontSize: '0.75rem',
          height: 20,
        }}
      />
    </Box>
  );
}

export function BrandLoginMock({ config, editMode, viewport }: BrandLoginMockProps) {
  const primary = config.colors.primary || '#1d4ed8';
  const background = config.colors.background || '#ffffff';
  const text = config.colors.text || '#1a1a1a';
  // Spec 0022 — card border color (only applied when set; '' = no explicit border)
  const border = config.colors.border || '';
  const primaryHover = config.primaryColorHover || primary;
  const body = config.fonts.body || 'system-ui, sans-serif';
  const heading = config.fonts.heading || body;
  const template = config.template || 'centered';
  // Title fallback chain: copyStrings.login_title → shortName → displayName
  const title = config.copyStrings?.login_title || config.shortName || config.displayName;
  const hasGoogle = config.authProviders.includes('google');

  return (
    <Box
      {...(viewport && { 'data-testid': 'preview-viewport' })}
      sx={{
        fontFamily: body,
        color: text,
        position: 'relative',
        // Viewport mode: fixed 1440×900 desktop box (flex column so the template
        // region fills the height). Window mode: fill the real window.
        ...(viewport
          ? { display: 'flex', flexDirection: 'column', overflow: 'hidden' }
          : { minHeight: '100vh' }),
      }}
      style={viewport ? { width: viewport.width, height: viewport.height } : undefined}
    >
      {/* Render based on template */}
      {template === 'split-hero' ? (
        <SplitHeroTemplate
          config={config}
          title={title}
          primary={primary}
          primaryHover={primaryHover}
          text={text}
          border={border}
          heading={heading}
          hasGoogle={hasGoogle}
          {...(editMode && { editMode })}
          {...(viewport && { viewport })}
        />
      ) : template === 'backdrop' ? (
        <BackdropTemplate
          config={config}
          title={title}
          primary={primary}
          primaryHover={primaryHover}
          text={text}
          border={border}
          heading={heading}
          hasGoogle={hasGoogle}
          {...(editMode && { editMode })}
          {...(viewport && { viewport })}
        />
      ) : (
        <CenteredTemplate
          config={config}
          title={title}
          primary={primary}
          primaryHover={primaryHover}
          background={background}
          text={text}
          border={border}
          heading={heading}
          hasGoogle={hasGoogle}
          {...(editMode && { editMode })}
          {...(viewport && { viewport })}
        />
      )}
    </Box>
  );
}

interface TemplateProps {
  config: BrandMockConfig;
  title: string;
  primary: string;
  primaryHover: string;
  background?: string;
  text: string;
  border: string;
  heading: string;
  hasGoogle: boolean;
  editMode?: EditModeHooks;
  viewport?: { width: number; height: number };
}

// Fill the parent's height: in viewport mode grow inside the fixed 900px box;
// in window mode fall back to 100vh.
function regionFillSx(viewport?: { width: number; height: number }) {
  return viewport ? { flexGrow: 1, minHeight: 0 } : { minHeight: '100vh' };
}

function CenteredTemplate(props: TemplateProps) {
  const {
    config,
    title,
    primary,
    primaryHover,
    background,
    text,
    border,
    heading,
    hasGoogle,
    editMode,
    viewport,
  } = props;

  return (
    <Slot field="pageBackground" fill={!!viewport} {...(editMode && { editMode })}>
      <Box
        sx={{
          ...regionFillSx(viewport),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: background,
          py: 8,
          px: 2,
        }}
      >
        <Slot field="cardBorder" {...(editMode && { editMode })}>
          <Paper
            sx={{
              p: 4,
              maxWidth: 400,
              width: '100%',
              borderRadius: 2,
              ...(border && { border: '1px solid', borderColor: border }),
            }}
          >
            <LoginCard
              config={config}
              title={title}
              primary={primary}
              primaryHover={primaryHover}
              text={text}
              border={border}
              heading={heading}
              hasGoogle={hasGoogle}
              {...(editMode && { editMode })}
            />
          </Paper>
        </Slot>
      </Box>
    </Slot>
  );
}

function SplitHeroTemplate(props: TemplateProps) {
  const {
    config,
    title,
    primary,
    primaryHover,
    text,
    border,
    heading,
    hasGoogle,
    editMode,
    viewport,
  } = props;

  // Hero background: dark gradient fallback when no heroImageUrl
  const heroBackground = config.heroImageUrl
    ? `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url("${config.heroImageUrl}") center / cover no-repeat`
    : `linear-gradient(135deg, ${primary} 0%, rgba(0,0,0,0.85) 100%)`;

  // Hero panel fills its half (the hero-side wrapper owns width / responsive hide).
  const heroPanel = (
    <Box
      aria-hidden
      sx={{
        flexGrow: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 2,
        p: 8,
        color: 'common.white',
        background: heroBackground,
      }}
    >
      {config.logoUrl && (
        <Slot field="logo" {...(editMode && { editMode })}>
          <Box
            component="img"
            src={config.logoUrl}
            alt=""
            sx={{ height: 48, width: 'auto', maxWidth: 220, objectFit: 'contain', mb: 2 }}
          />
        </Slot>
      )}
      {config.displayName && (
        <Typography variant="h3" sx={{ fontFamily: heading, fontWeight: 700 }}>
          {config.displayName}
        </Typography>
      )}
      {config.tagline && (
        <Slot field="tagline" {...(editMode && { editMode })}>
          <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.95 }}>
            {config.tagline}
          </Typography>
        </Slot>
      )}
      {config.heroCopy && (
        <Slot field="heroCopy" {...(editMode && { editMode })}>
          <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 460 }}>
            {config.heroCopy}
          </Typography>
        </Slot>
      )}
      {/* Spec 0022 — location (split-hero only, per saas_login SplitHero.tsx:71-75) */}
      {config.location && (
        <Slot field="location" {...(editMode && { editMode })}>
          <Typography variant="body2" sx={{ opacity: 0.85, mt: 2 }}>
            {config.location}
          </Typography>
        </Slot>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        ...regionFillSx(viewport),
        display: 'flex',
        flexDirection: viewport ? 'row' : { xs: 'column', md: 'row' },
      }}
    >
      {/* Hero side — left half, full height (≈50% of the viewport box at desktop
          width, per saas_login SplitHero.tsx). Hidden below md in window mode. */}
      <Box
        data-testid="hero-side"
        sx={{
          display: viewport ? 'flex' : { xs: 'none', md: 'flex' },
          ...(viewport ? { flexShrink: 0 } : { flex: 1 }),
        }}
        style={viewport ? { width: viewport.width / 2 } : undefined}
      >
        {/* logo height 48px per saas_login SplitHero.tsx */}
        <Slot field="heroImage" fill={!!viewport} {...(editMode && { editMode })}>
          {heroPanel}
        </Slot>
      </Box>

      {/* Card column — right half */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 'sm', py: { xs: 4, sm: 8 }, px: 2 }}>
          {/* Logo is hidden under split-hero per AuthCard template-aware logic */}
          <Slot field="cardBorder" {...(editMode && { editMode })}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 3, sm: 4 },
                width: '100%',
                borderRadius: 2,
                ...(border && { border: '1px solid', borderColor: border }),
              }}
            >
              <LoginCardContent
                config={config}
                title={title}
                primary={primary}
                primaryHover={primaryHover}
                text={text}
                border={border}
                heading={heading}
                hasGoogle={hasGoogle}
                {...(editMode && { editMode })}
              />
            </Paper>
          </Slot>
        </Box>
      </Box>
    </Box>
  );
}

function BackdropTemplate(props: TemplateProps) {
  const {
    config,
    title,
    primary,
    primaryHover,
    text,
    border,
    heading,
    hasGoogle,
    editMode,
    viewport,
  } = props;

  // Hero background: dark gradient fallback when no heroImageUrl
  const heroBackground = config.heroImageUrl
    ? `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url("${config.heroImageUrl}") center / cover no-repeat`
    : `linear-gradient(135deg, ${primary} 0%, rgba(0,0,0,0.85) 100%)`;

  const content = (
    <Box
      sx={{
        ...regionFillSx(viewport),
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: heroBackground,
        backgroundAttachment: 'fixed',
        py: 8,
        px: 2,
      }}
    >
      <Box sx={{ maxWidth: 'sm', width: '100%' }}>
        {/* Logo above card (per AuthCard; max 240×64 contain) */}
        {config.logoUrl && (
          <Slot field="logo" {...(editMode && { editMode })}>
            <Box
              component="img"
              src={config.logoUrl}
              alt={`${config.displayName} logo`}
              sx={{
                maxWidth: 240,
                maxHeight: 64,
                objectFit: 'contain',
                display: 'block',
                mx: 'auto',
                mb: 3,
              }}
            />
          </Slot>
        )}
        <Slot field="cardBorder" {...(editMode && { editMode })}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 3, sm: 4 },
              width: '100%',
              borderRadius: 2,
              ...(border && { border: '1px solid', borderColor: border }),
            }}
          >
            <LoginCardContent
              config={config}
              title={title}
              primary={primary}
              primaryHover={primaryHover}
              text={text}
              border={border}
              heading={heading}
              hasGoogle={hasGoogle}
              {...(editMode && { editMode })}
            />
          </Paper>
        </Slot>
      </Box>
    </Box>
  );

  return (
    <Slot field="heroImage" fill={!!viewport} {...(editMode && { editMode })}>
      {content}
    </Slot>
  );
}

interface LoginCardProps {
  config: BrandMockConfig;
  title: string;
  primary: string;
  primaryHover: string;
  text: string;
  border: string;
  heading: string;
  hasGoogle: boolean;
  editMode?: EditModeHooks;
}

/** LoginCard — centered template version with logo above title */
function LoginCard(props: LoginCardProps) {
  const { config, editMode } = props;

  const logo = config.logoUrl ? (
    <Box
      component="img"
      src={config.logoUrl}
      alt={`${config.displayName} logo`}
      sx={{ maxWidth: 240, maxHeight: 64, objectFit: 'contain', display: 'block', mx: 'auto' }}
    />
  ) : null;

  return (
    <Stack spacing={2}>
      {/* Logo above title (max 240×64 contain per saas_login BrandedLogo.tsx) */}
      {logo && (
        <Slot field="logo" {...(editMode && { editMode })}>
          {logo}
        </Slot>
      )}
      <LoginCardContent {...props} />
    </Stack>
  );
}

/** LoginCardContent — form fields, buttons, links */
function LoginCardContent(props: LoginCardProps) {
  const { t } = useCopy();
  const { config, title, primary, primaryHover, text, heading, hasGoogle, editMode } = props;
  // Subtitle copy mirrors saas_login AuthCard/Login.tsx: "Sign in to your {brand} account"
  // where {brand} is shortName (falling back to displayName).
  const subtitleName = config.shortName || config.displayName;
  // Spec 0030 — the guest-signup link renders when the toggle is on (the URL lives
  // on the product and is composed server-side; the mock link is inert).
  const guestActive = !!config.guestSignupEnabled;

  return (
    <Stack spacing={2}>
      {/* Heading = login-title text slot; colors.text rides in the same popover
          (Spec 0022 Part C overlap resolution). Color via inline style so the
          live mock update is observable. */}
      <Slot field="heading" {...(editMode && { editMode })}>
        <Typography
          variant="h5"
          sx={{ textAlign: 'center', fontWeight: 600, mb: 0.5 }}
          style={{ color: text, fontFamily: heading }}
        >
          {title}
        </Typography>
      </Slot>
      {/* Subtitle = short-name text slot (saas_login AuthCard subtitle) */}
      <Slot field="subtitle" {...(editMode && { editMode })}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            mb: 1,
          }}
        >
          {t('brands.preview.subtitle', 'Sign in to your {{brand}} account', {
            brand: subtitleName,
          })}
        </Typography>
      </Slot>
      {/* Email field */}
      <TextField
        fullWidth
        disabled
        label={t('brands.preview.email', 'Email')}
        placeholder="user@example.com"
        size="small"
      />
      {/* Password field */}
      <TextField
        fullWidth
        disabled
        label={t('brands.preview.password', 'Password')}
        type="password"
        size="small"
      />
      {/* Remember me */}
      <FormControlLabel
        control={<Checkbox disabled size="small" />}
        label={t('brands.preview.rememberMe', 'Remember me')}
      />
      {/* Sign in button = color slot (colors.primary + primaryColorHover) */}
      <Slot field="signInButton" {...(editMode && { editMode })}>
        <Button
          fullWidth
          disabled
          variant="contained"
          sx={{
            bgcolor: primary,
            color: '#fff',
            '&:hover': { bgcolor: primaryHover },
            '&.Mui-disabled': { bgcolor: primary, color: '#fff', opacity: 0.7 },
          }}
        >
          {t('brands.preview.signIn', 'Sign in')}
        </Button>
      </Slot>
      {/* Google sign-in = toggle slot (authProviders). Ghosted when google is off;
          in truthful renders (no editMode) off means absent. Spec 0023. */}
      {(hasGoogle || editMode) && (
        <Slot field="googleButton" {...(editMode && { editMode })}>
          <Button fullWidth disabled variant="outlined">
            {t('brands.preview.continueWithGoogle', 'Continue with Google')}
          </Button>
        </Slot>
      )}
      {/* Links — forgot-password + signup, each a ghosted-when-off toggle slot */}
      <Stack
        direction="row"
        spacing={2}
        sx={{
          justifyContent: 'center',
          mt: 1,
        }}
      >
        {(config.passwordResetEnabled || editMode) && (
          <Slot field="forgotPassword" {...(editMode && { editMode })}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('brands.preview.forgotPassword', 'Forgot password?')}
            </Typography>
          </Slot>
        )}
        {(config.signupEnabled || editMode) && (
          <Slot field="signupLink" {...(editMode && { editMode })}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('brands.preview.signUp', 'Sign up')}
            </Typography>
          </Slot>
        )}
      </Stack>
      {/* Guest-signup link (saas_login Login.tsx — shown when the toggle is on) =
          ghosted-when-off toggle slot. Label = product label ?? default. Spec 0030. */}
      {(guestActive || editMode) && (
        <Slot field="guestSignup" {...(editMode && { editMode })}>
          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {config.guestSignupLabel || t('brands.preview.guestSignup', 'Continue as guest')}
            </Typography>
          </Box>
        </Slot>
      )}
    </Stack>
  );
}
